/**
 * CSV export for the shipper "My Loads" page.
 *
 * Builds one row per load from the data ALREADY loaded by the page (which is
 * scoped server-side by RLS to the signed-in user's own / company loads), so
 * this adds no new data access.
 */

import { getLocalTodayYMD } from "@/lib/dateOnly";

export interface ExportLoadRow {
  load_number: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  pickup_date: string | null;
  equipment_type: string | null;
  load_type?: string | null;
  visibility_mode?: string | null;
  status: string;
  bid_deadline: string | null;
}

export interface ExportBidSummary {
  bidCount: number;
  lowestBid: number | null;
  highestBid: number | null;
  sum: number;
}

const HEADERS = [
  "Ref/Load #",
  "Origin",
  "Destination",
  "Pickup Date",
  "Equipment",
  "Load Type",
  "Visibility",
  "Status",
  "Deadline",
  "Number of Bids",
  "Lowest Bid",
  "Average Bid",
  "Highest Bid",
];

const escapeCell = (value: string | number | null | undefined): string => {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const formatVisibility = (mode?: string | null): string => {
  switch (mode) {
    case "open_market": return "Open Market";
    case "private_only": return "Private Network";
    case "both": return "Open Market + Private";
    default: return "";
  }
};

const formatLoadType = (value?: string | null): string => {
  if (!value) return "";
  const v = value.toLowerCase();
  if (v === "ftl" || v === "full" || v === "full_truckload") return "FTL";
  if (v === "ltl" || v === "partial") return "LTL";
  return value.toUpperCase();
};

const formatStatus = (value: string): string =>
  value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const formatDeadline = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function buildMyLoadsCsv(
  loads: ExportLoadRow[],
  bidStats: Record<string, ExportBidSummary | undefined>,
  idOf: (load: ExportLoadRow, index: number) => string,
  formatEquipment: (value: string | null | undefined) => string,
): string {
  const lines: string[] = [HEADERS.map(escapeCell).join(",")];

  loads.forEach((load, i) => {
    const stats = bidStats[idOf(load, i)];
    const count = stats?.bidCount ?? 0;
    const avg = count > 0 && stats ? Math.round(stats.sum / count) : null;

    lines.push([
      load.load_number,
      [load.origin_city, load.origin_state].filter(Boolean).join(", "),
      [load.destination_city, load.destination_state].filter(Boolean).join(", "),
      load.pickup_date ?? "",
      formatEquipment(load.equipment_type),
      formatLoadType(load.load_type),
      formatVisibility(load.visibility_mode),
      formatStatus(load.status),
      formatDeadline(load.bid_deadline),
      count,
      count > 0 && stats?.lowestBid != null ? stats.lowestBid : "",
      avg ?? "",
      count > 0 && stats?.highestBid != null ? stats.highestBid : "",
    ].map(escapeCell).join(","));
  });

  return lines.join("\r\n");
}

/** Triggers a browser download of the CSV (BOM prepended so Excel reads UTF-8). */
export function downloadCsv(csv: string, filename?: string): void {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `my-loads-${getLocalTodayYMD()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
